import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Save, Settings } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const SECTIONS = [
  { key: "home_hero", title: "Home Hero", fields: [
    { name: "eyebrow", label: "Eyebrow", type: "text" },
    { name: "title", label: "Title", type: "text" },
    { name: "subtitle", label: "Subtitle", type: "textarea" },
    { name: "cta_text", label: "Button Text", type: "text" },
  ]},
  { key: "about", title: "About the Trust", fields: [
    { name: "heading", label: "Heading", type: "text" },
    { name: "body", label: "Body", type: "textarea", rows: 5 },
  ]},
  { key: "vision_mission", title: "Vision, Mission & Objectives", fields: [
    { name: "vision", label: "Vision", type: "textarea" },
    { name: "mission", label: "Mission", type: "textarea" },
    { name: "objectives", label: "Objectives", type: "textarea", rows: 5 },
  ]},
  { key: "founder", title: "Founder", fields: [
    { name: "name", label: "Name", type: "text" },
    { name: "title", label: "Title", type: "text" },
    { name: "bio", label: "Biography", type: "textarea", rows: 5 },
  ]},
  { key: "contact", title: "Contact & Social", fields: [
    { name: "address", label: "Address", type: "textarea" },
    { name: "phone", label: "Phone", type: "text" },
    { name: "email", label: "Email", type: "text" },
    { name: "whatsapp", label: "WhatsApp Number", type: "text" },
    { name: "map_embed", label: "Google Maps Embed URL", type: "text" },
    { name: "map_link", label: "Get Directions Link (Google Maps share link)", type: "text" },
    { name: "facebook", label: "Facebook URL", type: "text" },
    { name: "instagram", label: "Instagram URL", type: "text" },
    { name: "youtube", label: "YouTube URL", type: "text" },
  ]},
  { key: "transparency", title: "Transparency Details", fields: [
    { name: "intro", label: "Intro Text", type: "textarea" },
    { name: "note", label: "Note (shown above documents)", type: "textarea" },
  ]},
];
const SECTIONS_I18N = SECTIONS.map((s) => (s.key === "contact" ? s : {
  ...s, fields: s.fields.flatMap((f) => [f, { ...f, name: `${f.name}_hi`, label: `${f.label} (हिन्दी)` }]),
}));


function SectionCard({ section, value, onSaved }) {
  const [form, setForm] = useState(value || {});
  const [saving, setSaving] = useState(false);
  const { logout } = useAuth();
  useEffect(() => { setForm(value || {}); }, [value]);

  const save = async () => {
    setSaving(true);
    const payload = Object.fromEntries(section.fields.map((f) => [f.name, form[f.name] || ""]));
    try { await api.put(`/admin/content/${section.key}`, payload); toast.success(`${section.title} saved`); onSaved(); }
    catch (err) {
      if (err.response?.status === 401) { toast.error("Session expired, please log in again"); logout(); }
      else { toast.error(err.response?.data?.detail || "Save failed"); }
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-white rounded-xl border border-[#E2DAD0] p-6" data-testid={`content-section-${section.key}`}>
      <h3 className="font-display text-xl font-semibold text-[#194324] mb-4">{section.title}</h3>
      <div className="space-y-4">
        {section.fields.map((f) => (
          <div key={f.name} className="space-y-2">
            <Label>{f.label}</Label>
            {f.type === "textarea"
              ? <Textarea value={form[f.name] || ""} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })} rows={f.rows || 3} data-testid={`content-${section.key}-${f.name}`} />
              : <Input value={form[f.name] || ""} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })} data-testid={`content-${section.key}-${f.name}`} />}
          </div>
        ))}
      </div>
      <Button onClick={save} disabled={saving} className="mt-5 bg-[#194324] hover:bg-[#2D623A] text-white" data-testid={`content-save-${section.key}`}>
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-2" /> Save</>}
      </Button>
    </div>
  );
}

export default function ContentManager() {
  const [content, setContent] = useState(null);
  const load = () => api.get("/admin/content").then((r) => setContent(r.data)).catch(() => setContent({}));
  useEffect(() => { load(); }, []);

  return (
    <div data-testid="admin-content-page">
      <div className="mb-6 flex items-center gap-3">
        <Settings className="h-6 w-6 text-[#C59B27]" />
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#B38728] font-mono">CMS</p>
          <h1 className="font-display text-3xl font-semibold text-[#194324]">Site Content</h1>
        </div>
      </div>
      {content === null ? (
        <div className="p-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#C59B27]" /></div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {SECTIONS_I18N.map((s) => <SectionCard key={s.key} section={s} value={content[s.key]} onSaved={load} />)}
        </div>
      )}
    </div>
  );
}
